// PageDescription: 비급여 가격 지도 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import NonBenefitMapTemplate from '@/components/templates/nonBenefit/map/NonBenefitMapTemplate';
import { NonBenefitMapQueries } from '@/entities/non-benefit';
import useNonBenefitMapNearByPharmacySearchTabs from '@/hooks/nonBenefit/useNonBenefitMapNearbyPharmacySearchTabs';
import useNonBenefitMapViewModel from '@/hooks/nonBenefit/useNonBenefitMapViewModel';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { addQueryString } from '@/utils/url';

const NonBenefitMapPages = ({ client }: CustomPageProps) => {
  const viewModel = useNonBenefitMapViewModel(client);
  const { treatmentMedicineId, diagnosisTypeId, treatmentMedicineUnitId, treatmentMedicineStrengthId } =
    viewModel.queries;

  const selectedMedicine = viewModel.selectedMedicine;

  const title = `${selectedMedicine?.treatmentMedicineSeoTitle ?? ''}`;
  const description = `${selectedMedicine?.treatmentMedicineSeoDescription ?? ''}`;

  const canonical = `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.NON_BENEFIT.MAP.path}${addQueryString({
    diagnosisTypeId,
    treatmentMedicineId,
    treatmentMedicineUnitId,
    treatmentMedicineStrengthId,
  })}`;

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        openGraph={{
          title,
          description: description,
          url: canonical,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_MAIN_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <NonBenefitMapTemplate
        client={client}
        viewModel={viewModel}
        title={`${selectedMedicine?.treatmentProductMedicineName} 최저가 약국 찾기`}
      />
    </>
  );
};

export default NonBenefitMapPages;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const queryClient = new QueryClient();
  const queries = ctx.query as NonBenefitMapQueries;

  const tabs = await useNonBenefitMapNearByPharmacySearchTabs.fetchQuery(queryClient);

  const defaultDiagnosisTypeId = tabs?.[0]?.diagnosisTypeId;

  if (!defaultDiagnosisTypeId) {
    return {
      redirect: {
        permanent: true,
        destination: ROUTES.NON_BENEFIT.HOME.path,
      },
    };
  }

  const selectedTab = tabs.find((tab) => tab.diagnosisTypeId === queries.diagnosisTypeId);

  /* 쿼리가 없는 경우 리다이렉트 */
  if (!queries.diagnosisTypeId || !selectedTab) {
    const defaultTreatmentMedicine = tabs[0].treatmentMedicines[0];

    const defaultTreatmentMedicineId = defaultTreatmentMedicine.treatmentMedicineId;

    const defaultDispensationUnit =
      defaultTreatmentMedicine.dispensationUnits.find((unit) => unit.isDefault) ||
      defaultTreatmentMedicine.dispensationUnits[0];

    const defaultTreatmentMedicineStrengthId =
      defaultTreatmentMedicine.treatmentMedicineStrengths.length > 0
        ? defaultTreatmentMedicine.treatmentMedicineStrengths.find((strength) => strength.isDefault) ||
          defaultTreatmentMedicine.treatmentMedicineStrengths[0]
        : null;

    return {
      redirect: {
        permanent: true,
        destination:
          ROUTES.NON_BENEFIT.MAP.path +
          addQueryString({
            diagnosisTypeId: defaultDiagnosisTypeId,
            treatmentMedicineId: defaultTreatmentMedicineId,
            treatmentMedicineUnitId: defaultDispensationUnit.treatmentMedicineUnitId,
            treatmentMedicineStrengthId: defaultTreatmentMedicineStrengthId?.treatmentMedicineStrengthId,
          }),
      },
    };
  }

  if (!queries.treatmentMedicineId) {
    const selectedDiagnosisTypeId = selectedTab?.diagnosisTypeId;
    const defaultTreatmentMedicine = selectedTab?.treatmentMedicines[0];

    const defaultTreatmentMedicineId = defaultTreatmentMedicine.treatmentMedicineId;
    const defaultDispensationUnit =
      defaultTreatmentMedicine.dispensationUnits.find((unit) => unit.isDefault) ||
      defaultTreatmentMedicine.dispensationUnits[0];

    const defaultTreatmentMedicineStrengthId =
      defaultTreatmentMedicine.treatmentMedicineStrengths.length > 0
        ? defaultTreatmentMedicine.treatmentMedicineStrengths.find((strength) => strength.isDefault) ||
          defaultTreatmentMedicine.treatmentMedicineStrengths[0]
        : null;

    return {
      redirect: {
        permanent: true,
        destination:
          ROUTES.NON_BENEFIT.MAP.path +
          addQueryString({
            diagnosisTypeId: selectedDiagnosisTypeId,
            treatmentMedicineId: defaultTreatmentMedicineId,
            treatmentMedicineUnitId: defaultDispensationUnit.treatmentMedicineUnitId,
            treatmentMedicineStrengthId: defaultTreatmentMedicineStrengthId?.treatmentMedicineStrengthId,
          }),
      },
    };
  }

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};
